import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Card, Form, InputNumber, Button, Table, Space, Typography, Select, DatePicker, Row, Col, Tag } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../../services/api';
import { showSuccessMessage, showErrorMessage } from '../../services/messages';
import GeneralContext from '../../context/GeneralContext/GeneralContext';
import EUserTypes from '../../enums/EUserTypes';
import { ScreenHeader, QuantityStep, FLOW_COLORS, primaryButtonStyle, BackToHome } from '../../components/drum/WizardParts';

const { Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * Painel de Recarga (RC): registro diário da quantidade que sobrou para o dia seguinte.
 * - Uma única RC por dia.
 * - Operadores só editam a RC do próprio dia; Admin edita qualquer dia.
 * - Toda alteração gera AuditLog no back-end.
 *
 * No PWA (mobile) este painel mostra apenas o saldo de RC de hoje. O fluxo
 * "Adicionar RC à máquina" (consumo por máquina) é um menu/rota separada
 * (RcConsumptionPanel). No desktop (admin) mostra também o histórico filtrável
 * de adições de RC por máquina.
 *
 * Prop `mobile` (opcional): quando true (operadores no PWA), exibe UI vertical,
 * inputs full-width grandes e oculta o histórico. Quando não passada, deriva de
 * `!isAdmin` (operador → mobile).
 */
const RcPanel = ({ mobile: mobileProp }) => {
  const { loggedUser } = useContext(GeneralContext);
  const location = useLocation();
  const isAdmin = loggedUser?.type === EUserTypes.PANEL_ADMIN.code;
  const mobile = mobileProp !== undefined ? mobileProp : !isAdmin;
  const flowColor = location.state?.color || FLOW_COLORS.rc;

  const [form] = Form.useForm();
  const [consumptionForm] = Form.useForm();
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState(null);

  // Filtros do histórico de adições (admin).
  const [consumptions, setConsumptions] = useState({ rows: [], count: 0, totalPages: 0, currentPage: 1, pageSize: 20 });
  const [consumptionFilters, setConsumptionFilters] = useState({});
  const [machines, setMachines] = useState([]);
  const [users, setUsers] = useState([]);

  const isToday = (dateStr) => {
    const d = new Date(dateStr);
    const n = new Date();
    return d.toDateString() === n.toDateString();
  };

  const load = async () => {
    setLoading(true);
    try {
      const fetches = [api.operation.getRcToday()];
      if (!mobile) fetches.push(api.operation.listRc({ limit: 30 }));
      const [todayRes, histRes] = await Promise.all(fetches);
      // getRcToday retorna { date, rc } — rc pode ser null quando ainda não
      // há registro para o dia (ex.: primeiro registro do dia).
      const rcToday = todayRes.data?.rc || null;
      setToday(rcToday);
      if (!mobile) {
        const list = histRes?.data?.rows || histRes?.data || [];
        setHistory(Array.isArray(list) ? list : []);
      }
      form.setFieldsValue({ rc_liters: rcToday ? rcToday.rc_liters : 0 });
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao carregar RC.');
    } finally {
      setLoading(false);
    }
  };

  const loadMachines = async () => {
    try {
      const res = await api.machine.list({ page: 1, pageSize: 100 });
      setMachines(res.data?.rows || []);
    } catch (e) { /* silencioso */ }
  };

  const loadUsers = async () => {
    try {
      const res = await api.user.list({ limit: 9999 });
      const list = res.data || [];
      setUsers(Array.isArray(list) ? list : (list.rows || []));
    } catch (e) { /* silencioso */ }
  };

  const loadConsumptions = async (params = {}) => {
    try {
      const res = await api.operation.listRcConsumptions(params);
      setConsumptions(res.data || { rows: [], count: 0, totalPages: 0, currentPage: 1, pageSize: 20 });
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao carregar histórico de adições de RC.');
    }
  };

  useEffect(() => {
    load();
    loadMachines();
    if (!mobile) {
      loadUsers();
      loadConsumptions({ page: 1, pageSize: 20 });
    }
  }, []);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      if (today && today.id) {
        await api.operation.updateRc(today.id, { rc_liters: values.rc_liters });
      } else {
        await api.operation.createRc({ rc_liters: values.rc_liters });
      }
      showSuccessMessage('RC salva com sucesso!');
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao salvar RC.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSave = async (record) => {
    try {
      await api.operation.updateRc(record.id, { rc_liters: editValue });
      showSuccessMessage('RC atualizada!');
      setEditingId(null);
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao atualizar RC.');
    }
  };

  const handleConsumptionTableChange = (pagination) => {
    const params = { ...consumptionFilters, page: pagination.current, pageSize: pagination.pageSize };
    setConsumptionFilters(params);
    loadConsumptions(params);
  };

  const handleConsumptionFilter = (values) => {
    const params = { page: 1, pageSize: consumptions.pageSize };
    if (values.dateRange && values.dateRange.length === 2) {
      params.startDate = values.dateRange[0].toISOString();
      params.endDate = values.dateRange[1].toISOString();
    }
    if (values.machineId) params.machineId = values.machineId;
    if (values.userId) params.userId = values.userId;
    if (values.source) params.source = values.source;
    setConsumptionFilters(params);
    loadConsumptions(params);
  };

  const handleConsumptionFilterClear = () => {
    consumptionForm.resetFields();
    setConsumptionFilters({});
    loadConsumptions({ page: 1, pageSize: consumptions.pageSize });
  };

  const defaultRange = useMemo(() => {
    const end = dayjs().endOf('day');
    const start = end.subtract(7, 'day').startOf('day');
    return [start, end];
  }, []);

  const consumptionColumns = [
    {
      title: 'Data/hora',
      dataIndex: 'occurred_at',
      key: 'occurred_at',
      width: 170,
      render: v => v ? new Date(v).toLocaleString('pt-BR') : '—',
    },
    {
      title: 'Tipo',
      dataIndex: 'source',
      key: 'source',
      width: 110,
      render: (v) => v === 'YELLOW_SOLVENT' ? <Tag color="gold">Amarelo</Tag> : <Tag color="blue">RC</Tag>
    },
    {
      title: 'Máquina',
      key: 'machine',
      width: 140,
      render: (_, r) => r.machine ? <Tag color="orange">{r.machine.code}</Tag> : '—'
    },
    { title: 'Litros', dataIndex: 'liters', key: 'liters', width: 100 },
    { title: 'Observação', dataIndex: 'observation', key: 'observation', render: v => v || '—' },
    { title: 'Usuário', key: 'user', render: (_, r) => r.user?.full_name || '—' },
  ];

  const columns = [
    { title: 'Data', dataIndex: 'date', key: 'date', render: v => new Date(v).toLocaleDateString('pt-BR') },
    {
      title: 'RC (L)',
      key: 'rc_liters',
      render: (_, r) => editingId === r.id ? (
        <InputNumber value={editValue} onChange={setEditValue} min={0} step={0.1} decimalSeparator="," />
      ) : r.rc_liters
    },
    { title: 'Usuário', key: 'user', render: (_, r) => r.user?.full_name || '—' },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, r) => {
        const canEdit = isAdmin || isToday(r.date);
        if (!canEdit) return null;
        if (editingId === r.id) {
          return (
            <Space>
              <Button size="small" type="primary" icon={<SaveOutlined />} onClick={() => handleEditSave(r)} />
              <Button size="small" icon={<CloseOutlined />} onClick={() => setEditingId(null)} />
            </Space>
          );
        }
        return <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingId(r.id); setEditValue(r.rc_liters); }}>Editar</Button>;
      }
    }
  ];

  if (mobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <BackToHome color={flowColor} />
        <Card loading={loading}>
          <ScreenHeader
            title="Recarga (RC)"
            subtitle="Informe a quantidade que sobrou para o dia seguinte. 0 é um bom sinal!"
            color={flowColor}
          />
          <Form form={form} layout="vertical" onFinish={handleSave}>
            <QuantityStep
              form={form}
              name="rc_liters"
              label="RC de hoje (litros)"
              hint="Toque no campo e use o teclado numérico."
            />
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={saving}
                size="large"
                block
                style={primaryButtonStyle(flowColor)}
              >
                {today ? 'Atualizar RC de hoje' : 'Registrar RC de hoje'}
              </Button>
            </Form.Item>
          </Form>
          {today && (
            <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 8 }}>
              Registrado por {today.user?.full_name || '—'}
            </Text>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card title="Recarga (RC) de hoje" size="small">
        <Form form={form} layout="inline" onFinish={handleSave}>
          <Form.Item label="Quantidade de RC (L)" name="rc_liters" rules={[{ required: true }]}>
            <InputNumber min={0} step={0.1} decimalSeparator="," style={{ width: 180 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>
              {today ? 'Atualizar RC de hoje' : 'Registrar RC de hoje'}
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card title="Histórico recente" size="small">
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={history}
          columns={columns}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Card title="Histórico de adições de recarga por máquina" size="small">
        <Form form={consumptionForm} layout="vertical" onFinish={handleConsumptionFilter}>
          <Row gutter={16}>
            <Col xs={24} sm={10}>
              <Form.Item label="Período (default: últimos 7 dias)" name="dateRange" initialValue={defaultRange}>
                <RangePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={5}>
              <Form.Item label="Máquina" name="machineId">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Todas"
                  options={machines.map(m => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={3}>
              <Form.Item label="Tipo" name="source">
                <Select
                  allowClear
                  placeholder="Todos"
                  options={[
                    { value: 'RC', label: 'RC' },
                    { value: 'YELLOW_SOLVENT', label: 'Amarelo' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={3}>
              <Form.Item label="Usuário" name="userId">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Todos"
                  options={users.map(u => ({ value: u.id, label: u.full_name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={4} style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Space>
                <Button type="primary" htmlType="submit">Filtrar</Button>
                <Button onClick={handleConsumptionFilterClear}>Limpar</Button>
              </Space>
            </Col>
          </Row>
        </Form>
        <Table
          rowKey="id"
          size="small"
          dataSource={consumptions.rows}
          columns={consumptionColumns}
          pagination={{
            current: consumptions.currentPage,
            pageSize: consumptions.pageSize,
            total: consumptions.count,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} adições`,
          }}
          onChange={handleConsumptionTableChange}
          scroll={{ x: 700 }}
          locale={{ emptyText: 'Nenhuma adição de RC encontrada.' }}
        />
      </Card>
    </div>
  );
};

export default RcPanel;
